<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import dayjs, { Dayjs } from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

import ErrorModal from '@/components/common/ErrorModal.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import SparkBreadcrumb from '@/components/generic/SparkBreadcrumb.vue'
import { ROUTE_NAMES } from '@/router/route-names'
import { useMainStore } from '@/stores'
import { useGasStore } from '@/stores/gas'
import type { components } from '#/types/capi-generated'

import { generateId } from './utils'

type GasTicker = components['schemas']['GasContractsViewTickerEnum']
type GasUnit = components['schemas']['CAPIGasContractsUnitEnum']

interface TickerOption {
  label: string
  value: GasTicker
  checked: boolean
}

interface TickerGroup {
  label: string
  tickers: TickerOption[]
}

const tickerGroups = reactive<TickerGroup[]>([
  {
    label: 'TTF',
    tickers: [
      { label: 'Day-Ahead', value: 'sparkleba-ttf-da', checked: false },
      { label: 'Balance of Month', value: 'sparkleba-ttf-bom', checked: false },
      { label: 'Front', value: 'sparkleba-ttf-f', checked: false },
      { label: 'Forward', value: 'sparkleba-ttf-fo', checked: false },
    ]
  },
  {
    label: 'THE',
    tickers: [
      { label: 'Day-Ahead', value: 'sparkleba-the-da', checked: false },
      { label: 'Balance of Month', value: 'sparkleba-the-bom', checked: false },
      { label: 'Front', value: 'sparkleba-the-f', checked: false },
      { label: 'Forward', value: 'sparkleba-the-fo', checked: false },
    ]
  },
  {
    label: 'THE-TTF Spreads',
    tickers: [
      { label: 'Day-Ahead', value: 'sparkleba-the-ttf-da', checked: false },
      { label: 'Balance of Month', value: 'sparkleba-the-ttf-bom', checked: false },
      { label: 'Front', value: 'sparkleba-the-ttf-f', checked: false },
      { label: 'Forward', value: 'sparkleba-the-ttf-fo', checked: false },
    ]
  }
])

const unitOptions: { text: string; value: GasUnit }[] = [
  { text: '€/MWh', value: 'eur-per-mwh' },
  { text: '$/MMBtu', value: 'usd-per-mmbtu' },
]

const priceReleasesOptions = [
  { text: 'Latest', value: 'latest' },
  { text: 'Date Range', value: 'date-range' },
]

const startDate = ref<Dayjs>(dayjs().subtract(7, 'day'))
const endDate = ref<Dayjs>(dayjs())

const state = reactive<{
  unit: GasUnit
  priceReleasesFilter: string
  submitting: boolean
  errors: string[]
  showErrorModal: boolean
}>({
  unit: 'eur-per-mwh',
  priceReleasesFilter: 'latest',
  submitting: false,
  errors: [],
  showErrorModal: false,
})

const gasStore = useGasStore()
const mainStore = useMainStore()

const allTickers = computed(() => tickerGroups.flatMap((g) => g.tickers))

const someChecked = computed(() => allTickers.value.some((t) => t.checked))

const allChecked = computed(() => allTickers.value.every((t) => t.checked))

const isDateRangeValid = computed(() => {
  if (state.priceReleasesFilter !== 'date-range') return true
  const start = startDate.value.startOf('day')
  const end = endDate.value.endOf('day')
  if (end.isBefore(start)) return false
  return end.diff(start, 'year', true) <= 1
})

const onCheckAllChange = (e: any) => {
  const checked = e.target.checked
  tickerGroups.forEach((g) => g.tickers.forEach((t) => (t.checked = checked)))
}

const MAX_CELLS_PER_OPERATION = 1000000

async function loadData() {
  state.submitting = true
  state.errors = []

  try {
    const selectedTickers = allTickers.value.filter((t) => t.checked)

    const isLatest = state.priceReleasesFilter === 'latest'

    const results = await Promise.allSettled(
      selectedTickers.map((t) => {
        if (isLatest) {
          return gasStore.fetchGasContracts(t.value, state.unit, undefined, undefined, 1)
        } else {
          return gasStore.fetchGasContracts(t.value, state.unit, startDate.value.format('YYYY-MM-DD'), endDate.value.format('YYYY-MM-DD'))
        }
      })
    )

    let mergedColumns: string[] = []
    let mergedRows: string[][] = []

    results.forEach((result, index) => {
      const ticker = selectedTickers[index]
      if (result.status === 'rejected') {
        state.errors.push(
          `Error fetching ${ticker.value}: ${result.reason instanceof Error ? result.reason.message : 'Unknown error'}`
        )
      } else {
        const { columns, rows } = result.value
        if (columns.length > 0 && rows.length > 0) {
          if (mergedColumns.length === 0) {
            mergedColumns = columns
          }
          mergedRows = mergedRows.concat(rows)
        }
      }
    })

    if (mergedColumns.length > 0 && mergedRows.length > 0) {
      await pasteTableOntoExcel(mergedColumns, mergedRows)
    }
  } catch (err: unknown) {
    if (err instanceof Error) {
      state.errors.push(err.message)
    } else {
      state.errors.push('An unknown error occurred')
    }
  } finally {
    if (state.errors.length > 0) {
      state.showErrorModal = true
    }
    state.submitting = false
  }
}

async function pasteTableOntoExcel(columns: string[], rows: string[][]) {
  try {
    if (!window.Excel) {
      console.warn('Excel is not loaded')
      return
    }

    const COLUMNS_COUNT = columns.length
    const ROWS_COUNT = rows.length
    const totalCells = ROWS_COUNT * COLUMNS_COUNT

    if (totalCells > MAX_CELLS_PER_OPERATION) {
      console.warn(`Large dataset detected: ${totalCells} cells. Consider splitting into smaller batches.`)
    }

    await window.Excel.run(async (context) => {
      const app = context.workbook.application
      app.suspendScreenUpdatingUntilNextSync()
      app.suspendApiCalculationUntilNextSync()

      const workbook = context.workbook
      const sheet = workbook.worksheets.getActiveWorksheet()
      const activeRange = workbook.getSelectedRange()
      const firstCell = activeRange.getCell(0, 0)

      sheet.load('tables')
      activeRange.load('address')
      await context.sync()

      const existingTableCollection = sheet.tables
      const existingTables = existingTableCollection.items

      const tableDataRange = firstCell.getAbsoluteResizedRange(
        ROWS_COUNT + 1,
        COLUMNS_COUNT
      )

      if (existingTables && existingTables.length > 0) {
        const tablesToDelete: string[] = []
        const tableIntersections: { table: Excel.Table; intersection: Excel.Range }[] = []

        for (const existingTable of existingTables) {
          const range = existingTable.getRange()
          const intersection = range.getIntersectionOrNullObject(tableDataRange)
          intersection.load('address')
          tableIntersections.push({ table: existingTable, intersection })
        }

        await context.sync()

        for (const { table, intersection } of tableIntersections) {
          if (!intersection.isNullObject) {
            tablesToDelete.push(table.name)
          }
        }

        for (const tableName of tablesToDelete) {
          try {
            sheet.tables.getItem(tableName).delete()
          } catch (err) {
            state.errors.push(`Error deleting table ${tableName}: ${JSON.stringify(err)}`)
          }
        }

        if (tablesToDelete.length > 0) {
          await context.sync()
        }
      }

      const allTableData = [columns, ...rows]
      tableDataRange.values = allTableData

      const TABLE_NAME = 'GasTable' + generateId()
      const dataTable = sheet.tables.add(tableDataRange, true)
      dataTable.name = TABLE_NAME

      const headerRange = dataTable.getHeaderRowRange()
      const dataRange = dataTable.getDataBodyRange()

      dataTable.style = 'TableStyleLight1'

      headerRange.format.fill.color = '#2563eb'
      const headerFont = headerRange.format.font
      headerFont.color = '#FFFFFF'
      headerFont.size = 12
      headerFont.name = 'Mont'
      headerFont.bold = true

      dataRange.format.fill.color = '#FFFFFF'
      const dataFont = dataRange.format.font
      dataFont.color = '#000000'
      dataFont.name = 'Roboto'
      dataFont.bold = false

      if (Office.context.requirements.isSetSupported('ExcelApi', '1.2')) {
        const usedRange = sheet.getUsedRange()
        const usedRangeFormat = usedRange.format
        usedRangeFormat.autofitColumns()
        usedRangeFormat.autofitRows()
      }

      sheet.activate()
      await context.sync()
    })
  } catch (err: any) {
    console.error('Error in pasteTableOntoExcel:', err)
    state.errors.push(err.message)
  }
}

const router = useRouter()

function handleBreadcrumbInput(value: string) {
  router.push({ name: value })
}
</script>

<template>
  <Loader :modelValue="mainStore.isLoading">
    <div class="gas-page p-5 space-y-10">
      <section class="space-y-6">
        <PageHeader title="Get European Gas Pricing Data" />
        <SparkBreadcrumb
          :config="[
            { name: 'Products', icon: 'home-filled', value: ROUTE_NAMES.HOME },
            {
              name: 'European Gas',
              icon: 'gas',
              styleClasses: 'bg-gas text-white'
            }
          ]"
          @input="handleBreadcrumbInput"
        />
      </section>

      <a-form layout="vertical">
        <a-form-item label="Please select the contracts to include">
          <div class="px-3 py-1 border-1 border-gray-300 bg-white">
            <table class="table-fixed w-full theme-table">
              <thead>
                <tr>
                  <th class="text-left">Contract Id</th>
                  <th class="text-right w-[1rem]">
                    <a-checkbox
                      :checked="allChecked"
                      @change="onCheckAllChange"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in tickerGroups" :key="group.label">
                  <tr class="bg-gray-50">
                    <td colspan="2" class="py-2 font-semibold text-gray-900">
                      {{ group.label }}
                    </td>
                  </tr>
                  <tr
                    v-for="ticker in group.tickers"
                    :key="ticker.value"
                    class="hover:bg-gas-50 cursor-pointer"
                    @click="ticker.checked = !ticker.checked"
                  >
                    <td>
                      {{ ticker.value }}
                    </td>
                    <td class="text-right">
                      <a-checkbox :checked="ticker.checked" @click.stop="ticker.checked = !ticker.checked" />
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </a-form-item>

        <a-form-item :colon="false" label="Price Unit">
          <a-radio-group
            v-model:value="state.unit"
            button-style="solid"
            size="small"
          >
            <a-radio-button
              v-for="opt in unitOptions"
              :value="opt.value"
              :key="opt.value"
            >{{ opt.text }}</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item :colon="false" label="Releases">
          <a-radio-group
            class="w-full"
            v-model:value="state.priceReleasesFilter"
            size="small"
          >
            <a-radio-button
              v-for="opt in priceReleasesOptions"
              :value="opt.value"
              :key="opt.value"
            >{{ opt.text }}</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <div v-if="state.priceReleasesFilter === 'date-range'">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <a-form-item :colon="false" label="Start Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="startDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
            <div>
              <a-form-item :colon="false" label="End Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="endDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
          </div>
          <p class="text-sm text-amber-600 flex items-center gap-1 mb-4">
            <InfoCircleOutlined />
            <span>Maximum date range is 1 year</span>
          </p>
        </div>

        <div>
          <SparkButton
            class="w-full"
            size="lg"
            color="gas"
            :disabled="!someChecked || !isDateRangeValid"
            :loading="state.submitting"
            @click="loadData"
          >Insert data into selected cell</SparkButton>
        </div>
      </a-form>

      <ErrorModal
        v-model:visible="state.showErrorModal"
        title="Some contracts could not be retrieved"
        description="The following contracts encountered issues."
        :errors="state.errors"
      />
    </div>
  </Loader>
</template>

<style lang="scss">
.gas-page {
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: #2563eb !important;
    border-color: #2563eb !important;

    &:hover {
      background: #1D58D8 !important;
      border-color: #1D58D8 !important;
    }

    &::before {
      background-color: #2563eb !important;
    }
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #2563eb !important;
    border-color: #2563eb !important;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: #2563eb !important;
  }
}

.theme-table {
  thead th {
    @apply py-2 border-b-1 border-black;
  }

  tbody td {
    @apply py-1;
  }
}
</style>
